import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { CURRENCIES, formatMoney } from "@/lib/format";
import { toast } from "sonner";

const emptyForm = { name: "", currency: "TRY" };

export default function CashRegisters() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = async () => setItems((await api.get("/cash-registers")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (k) => { setEditingId(k.id); setForm({ name: k.name, currency: k.currency }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Kasa adı gerekli"); return; }
    try {
      if (editingId) {
        await api.put(`/cash-registers/${editingId}`, form);
        toast.success("Kasa güncellendi");
      } else {
        await api.post("/cash-registers", form);
        toast.success("Kasa oluşturuldu");
      }
      setOpen(false); setEditingId(null); setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const askDelete = (k) => setConfirmTarget(k);
  const doDelete = async () => {
    const k = confirmTarget;
    if (!k) return;
    setConfirmTarget(null);
    try {
      await api.delete(`/cash-registers/${k.id}`);
      toast.success("Kasa silindi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="cash-registers-page">
      <div className="flex items-center justify-between">
        <div><div className="text-xs uppercase tracking-widest text-zinc-500">Finans</div><h1 className="text-3xl mt-1">Kasalar</h1></div>
        <Button data-testid="new-cash-button" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800"><Plus size={16} className="mr-1"/>Yeni Kasa</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.length===0 && <div className="text-zinc-500 col-span-3">Henüz kasa yok</div>}
        {items.map(k => (
          <div key={k.id} className="group relative p-5 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors" data-testid={`cash-card-${k.id}`}>
            <Link to={`/kasalar/${k.id}`} className="block">
              <div className="text-xs uppercase tracking-widest text-zinc-500">{k.currency}</div>
              <div className="text-lg font-medium mt-1 pr-16">{k.name}</div>
              <div className={`mt-3 text-2xl number ${(k.balance||0)>=0?"pos":"neg"}`}>{formatMoney(k.balance, k.currency)}</div>
            </Link>
            <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                type="button"
                data-testid={`cash-edit-${k.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(k); }}
                className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900"
                title="Düzenle"
              ><Pencil size={14} /></button>
              <button
                type="button"
                data-testid={`cash-delete-${k.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); askDelete(k); }}
                className="p-1.5 rounded hover:bg-red-50 text-zinc-500 hover:text-red-600"
                title="Sil"
              ><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent data-testid="cash-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Kasayı Düzenle" : "Yeni Kasa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad</Label><Input data-testid="cash-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency: v})}>
                <SelectTrigger data-testid="cash-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>İptal</Button>
            <Button data-testid="cash-save-button" onClick={save} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTarget} onOpenChange={(v)=>{ if (!v) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid="cash-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Kasayı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmTarget?.name}" kasası silinsin mi? Bu işlem geri alınamaz. Kasa bakiyesi ve geçmiş hareketler etkilenmez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cash-delete-cancel">İptal</AlertDialogCancel>
            <AlertDialogAction data-testid="cash-delete-confirm-btn" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
